import { t } from '../../i18n';
/**
 * Display formatting.
 *
 * Every money and date string in the admin panel goes through here, so the
 * Indian conventions (2,2,3 digit grouping; the society timezone; dd MMM yyyy) are applied once
 * rather than re-derived per screen.
 */

/** Indian grouping: 1234567 -> 12,34,567. */
function groupIndian(digits: string): string {
  if (digits.length <= 3) return digits;
  const last3 = digits.slice(-3);
  const rest = digits.slice(0, -3);
  return `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${last3}`;
}

/**
 * Formats paise as rupees.
 *
 * Accepts a string because the API serialises `bigint` as a string — parsing it
 * into a JS number would silently lose precision above ₹90,000 crore, and more
 * importantly would reintroduce float rounding into money the UI displays.
 */
/**
 * The currency every un-prefixed money string is drawn in.
 *
 * Set once per session from the society's own dues currency, so the 45 call
 * sites that simply ask for "money" do not each have to carry it. Before this
 * the rupee and Indian 2,2,3 grouping were hard-coded here, which meant a
 * society billing in AED still saw ₹ on every screen.
 */
let displayCurrency = 'INR';

export function setDisplayCurrency(code: string): void {
  if (/^[A-Z]{3}$/.test(code)) displayCurrency = code;
}

export function getDisplayCurrency(): string {
  return displayCurrency;
}

/**
 * A currency the viewer has chosen to read amounts in, and the factor from the
 * real currency to it.
 *
 * This is a lens, never the truth: the money itself stays in the currency it
 * was billed in, and the figure is shown plainly in the chosen one at the
 * platform's daily rate — the same rate the checkout quotes with, so what is
 * read is what is charged. Anything actually payable asks for `exact: true`
 * and is never converted at all: a resident who pays a converted number pays
 * the wrong amount and the books do not balance. That guarantee is the one
 * that matters, and it is enforced at the call site, not by decorating the
 * text.
 */
let viewCurrency: string | null = null;
let viewFactor = 1;

export function setViewCurrency(code: string | null, factor = 1): void {
  viewCurrency = code && /^[A-Z]{3}$/.test(code) ? code : null;
  viewFactor = Number.isFinite(factor) && factor > 0 ? factor : 1;
}

/**
 * The sign for whatever currency is on screen — the viewer's chosen one if they
 * picked one, otherwise the society's.
 *
 * Labels like "Amount (₹)" used to carry the rupee in the text itself, which a
 * society billing in dirhams could not override. Derived from the code so the
 * label and the amounts beside it can never disagree. A currency with no sign
 * of its own answers with its code.
 */
export function currencySymbol(options: { own?: boolean } = {}): string {
  // An input box must be labelled with the currency its value is *stored* in,
  // never the one the viewer is reading in: picking INR as a reading aid put a
  // ₹ in front of a box whose number was still written as dollars. Displays
  // take the default and follow the reading choice, so a converted figure and
  // its symbol always agree.
  const code = options.own ? displayCurrency : (viewCurrency ?? displayCurrency);
  if (code === 'INR') return '₹';
  try {
    const parts = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((part) => part.type === 'currency')?.value ?? code;
  } catch {
    return code;
  }
}

export function getViewCurrency(): string | null {
  return viewCurrency;
}

export function formatMoney(
  paise: bigint | string | number,
  options: { decimals?: boolean; symbol?: boolean; compact?: boolean; exact?: boolean; currency?: string } = {},
): string {
  const { decimals = true, symbol = true, compact = false, exact = false, currency = displayCurrency } = options;

  // A viewer's chosen currency is a reading aid. An amount somebody will pay,
  // or that a ledger must reconcile, asks for `exact` and ignores it.
  if (!exact && viewCurrency && viewCurrency !== currency) {
    const base = typeof paise === 'bigint' ? paise : BigInt(paise || 0);
    const negative = base < 0n;
    const abs = negative ? -base : base;
    const major = (Number(abs) / 10 ** currencyDigits(currency)) * viewFactor;
    return `${negative ? '−' : ''}${formatMajor(major, viewCurrency, { decimals: compact ? false : decimals })}`;
  }

  const value = typeof paise === 'bigint' ? paise : BigInt(paise || 0);
  const negative = value < 0n;
  const abs = negative ? -value : value;

  // Lakh/crore and the 2,2,3 grouping are Indian conventions; applying them to
  // dollars would read as nonsense, so anything else goes through Intl.
  if (currency !== 'INR') {
    const major = Number(abs) / 10 ** currencyDigits(currency);
    const text = formatMajor(major, currency, { decimals: compact ? false : decimals });
    return `${negative ? '−' : ''}${symbol ? text : text.replace(/[^\d.,]/g, '').trim()}`;
  }

  if (compact) {
    return `${negative ? '−' : ''}${symbol ? '₹' : ''}${compactRupees(abs)}`;
  }

  const whole = groupIndian((abs / 100n).toString());
  const fraction = (abs % 100n).toString().padStart(2, '0');

  return `${negative ? '−' : ''}${symbol ? '₹' : ''}${whole}${decimals ? `.${fraction}` : ''}`;
}

/** ₹1.2L / ₹3.4Cr — for dashboard tiles where exact paise are noise. */
export function compactRupees(paise: bigint): string {
  const rupees = paise / 100n;

  if (rupees >= 10_000_000n) return `${divide(rupees, 10_000_000n, 2)}Cr`;
  if (rupees >= 100_000n) return `${divide(rupees, 100_000n, 2)}L`;
  if (rupees >= 1_000n) return `${divide(rupees, 1_000n, 1)}K`;
  return rupees.toString();
}

function divide(value: bigint, divisor: bigint, decimals: number): string {
  const scale = 10n ** BigInt(decimals);
  const scaled = (value * scale) / divisor;
  const whole = scaled / scale;
  const fraction = (scaled % scale).toString().padStart(decimals, '0');
  return `${whole}.${fraction}`.replace(/\.?0+$/, '');
}

// ── Dates. All rendered in the *society's* timezone regardless of the
//    viewer's, because a society's day is the society's day — a
//    treasurer checking in from Dubai must see the same due date
//    the resident in Jaipur sees. ──────────────────────────────────────────────

/**
 * The timezone every date helper renders in.
 *
 * Module state for the same reason as the date format below: `formatDate` is
 * called from table cells and CSV column definitions where a hook cannot go.
 * Set once by the shell — the society's own zone in the admin panel, the
 * platform's in the console — and until it is set the helpers render in the
 * browser's zone rather than in a zone guessed here.
 */
let displayTimezone: string | undefined;

export function setDisplayTimezone(timezone: string | null | undefined): void {
  displayTimezone = timezone || undefined;
}

export function getDisplayTimezone(): string | undefined {
  return displayTimezone;
}

/**
 * The platform-wide date format, chosen in the console.
 *
 * Module state rather than a hook, deliberately. `formatDate` is called from
 * about forty components, several of them inside table cell renderers and CSV
 * column definitions where a hook cannot go. Threading a context through all of
 * them to change a slash into a dash would be a large change with a large
 * chance of missing one — and a date format that applies to most of the app is
 * worse than one that applies to none, because nobody can tell which is which.
 *
 * Set once, on boot, by `useDateFormatSetting`.
 */
let platformPattern: DatePattern | null = null;

export function setPlatformDateFormat(pattern: DatePattern | null): void {
  platformPattern = pattern;
}

export function getPlatformDateFormat(): DatePattern | null {
  return platformPattern;
}

/**
 * A date, in whatever format the platform is configured for.
 *
 * `style` still decides how much detail is shown when no format has been
 * chosen: 'short' for lists, 'long' for a document, 'numeric' for an export.
 * Once a format is set it wins for 'short' and 'numeric' — but never for
 * 'long', which exists precisely for the places that want "3 April 2026"
 * spelled out and would read badly as 03/04/26.
 */
export function formatDate(value: string | Date | null | undefined, style: 'short' | 'long' | 'numeric' = 'short'): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';

  if (platformPattern && style !== 'long') return formatWithPattern(date, platformPattern);

  return new Intl.DateTimeFormat('en-IN', {
    timeZone: displayTimezone,
    day: 'numeric',
    month: style === 'long' ? 'long' : style === 'numeric' ? '2-digit' : 'short',
    year: 'numeric',
    ...(style === 'numeric' ? { day: '2-digit' } : {}),
  }).format(date);
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';

  // The date half follows the setting; the time half does not, because there
  // is only one sensible way to write a time and the console does not offer a
  // choice about it.
  if (platformPattern) return `${formatWithPattern(date, platformPattern)}, ${formatTime(date)}`;

  return new Intl.DateTimeFormat('en-IN', {
    timeZone: displayTimezone,
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

export function formatTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: displayTimezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

/**
 * "3 days ago", "in 2 hours".
 *
 * Coarse past a week on purpose — for anything older, the absolute date is what
 * someone actually wants.
 */
export function formatRelative(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';

  const deltaMs = date.getTime() - Date.now();
  const abs = Math.abs(deltaMs);

  if (abs < 45_000) return 'just now';
  if (abs > 30 * 86_400_000) return formatDate(date);

  const formatter = new Intl.RelativeTimeFormat('en-IN', { numeric: 'auto' });
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['minute', 60_000],
    ['hour', 3_600_000],
    ['day', 86_400_000],
    ['week', 604_800_000],
  ];

  let unit: Intl.RelativeTimeFormatUnit = 'minute';
  let divisor = 60_000;
  for (const [candidate, ms] of units) {
    if (abs >= ms) {
      unit = candidate;
      divisor = ms;
    }
  }

  return formatter.format(Math.round(deltaMs / divisor), unit);
}

/** Days between now and a date. Negative means overdue. */
export function daysUntil(value: string | Date | null | undefined): number | null {
  if (!value) return null;
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return null;
  return Math.ceil((date.getTime() - Date.now()) / 86_400_000);
}

// ── Text ────────────────────────────────────────────────────────────────────

/** `SOCIETY_ADMIN` -> `Society admin`. */
export function humaniseEnum(value: string | null | undefined): string {
  if (!value) return '—';
  const lower = value.toLowerCase().replace(/_/g, ' ');
  // The English wording is the dictionary key, so a status word reads in
  // the panel's language wherever an enum is shown.
  return t(lower.charAt(0).toUpperCase() + lower.slice(1));
}

export function formatPhone(e164: string | null | undefined): string {
  if (!e164) return '—';
  const match = /^\+91(\d{5})(\d{5})$/.exec(e164);
  return match ? `+91 ${match[1]} ${match[2]}` : e164;
}

export function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** index;
  return `${index === 0 ? value : value.toFixed(1)} ${units[index]}`;
}

export function initials(name: string, max = 2): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, max)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase();
}

// ── Configurable date format ────────────────────────────────────────────────

/**
 * Renders a date in one of the patterns offered in platform settings.
 *
 * Deliberately not a general date-format engine: it understands exactly the
 * five patterns the settings page offers, because a free-text pattern would
 * let someone save `DD/MM/YYY` and quietly break every date on every screen.
 *
 * Always rendered in the display timezone, like every other date helper here
 * — a society's day is the society's day, whatever zone the viewer is in.
 */
export type DatePattern = 'DD/MM/YYYY' | 'DD/MM/YY' | 'DD MMM YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';

/** Formatters are built per zone and kept — `Intl.DateTimeFormat` is costly to construct. */
const partsFormats = new Map<string, { parts: Intl.DateTimeFormat; month: Intl.DateTimeFormat }>();

function formatsFor(timeZone: string | undefined): { parts: Intl.DateTimeFormat; month: Intl.DateTimeFormat } {
  const key = timeZone ?? '';
  let found = partsFormats.get(key);
  if (!found) {
    found = {
      parts: new Intl.DateTimeFormat('en-GB', { timeZone, day: '2-digit', month: '2-digit', year: 'numeric' }),
      month: new Intl.DateTimeFormat('en-GB', { timeZone, month: 'short' }),
    };
    partsFormats.set(key, found);
  }
  return found;
}

export function formatWithPattern(
  value: string | Date | null | undefined,
  pattern: DatePattern,
): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';

  // `Intl` rather than getDate()/getMonth(): those read the *viewer's*
  // timezone, which is exactly the bug this module exists to avoid.
  const formats = formatsFor(displayTimezone);
  const parts = formats.parts.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';

  const day = get('day');
  const month = get('month');
  const year = get('year');

  switch (pattern) {
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`;
    case 'DD/MM/YY':
      return `${day}/${month}/${year.slice(-2)}`;
    case 'DD MMM YYYY':
      return `${day} ${formats.month.format(date)} ${year}`;
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    default:
      return `${day}/${month}/${year}`;
  }
}

// ── Other currencies ─────────────────────────────────────────────────────────

/** Currencies whose minor unit is not 1/100 — the rest of the world rounds to two places. */
const MINOR_DIGITS: Record<string, number> = { JPY: 0, KRW: 0, VND: 0, CLP: 0, ISK: 0, HUF: 2, KWD: 3, BHD: 3, OMR: 3, JOD: 3, TND: 3 };

export function currencyDigits(currency: string): number {
  return MINOR_DIGITS[currency] ?? 2;
}

/**
 * A plan price the society is quoted in its own currency.
 *
 * `basePaise` is the platform's price (base-currency minor units); `rate` is
 * how many units of `currency` one unit of the base buys. INR keeps the Indian
 * grouping; everything else uses the currency's own conventions.
 */
export function formatInCurrency(
  basePaise: bigint | string | number,
  currency: string,
  rate: number,
  options: { decimals?: boolean } = {},
): string {
  const base = typeof basePaise === 'bigint' ? basePaise : BigInt(basePaise || 0);
  if (currency === 'INR' && rate === 1) return formatMoney(base, { decimals: (options.decimals ?? true) || base % 100n !== 0n });
  const major = (Number(base) / 100) * rate;
  return formatMajor(major, currency, options);
}

/** An amount already in the currency's minor unit (what the gateway charged). */
export function formatMinor(amountMinor: bigint | string | number, currency: string): string {
  const minor = typeof amountMinor === 'bigint' ? amountMinor : BigInt(amountMinor || 0);
  if (currency === 'INR') return formatMoney(minor);
  return formatMajor(Number(minor) / 10 ** currencyDigits(currency), currency, {});
}

function formatMajor(major: number, currency: string, options: { decimals?: boolean }): string {
  // "No decimals" is a tidiness option for whole amounts; a converted price
  // like $112.92 must not be shown as $113 next to a dialog that charges $112.92.
  const whole = Math.abs(major - Math.round(major)) < 1e-9;
  const digits = options.decimals === false && whole ? 0 : currencyDigits(currency);
  try {
    return (
      new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en', {
        style: 'currency',
        currency,
        // The sign, in its shortest form: "$12.00", not "US$12.00". A currency
        // with no sign of its own — AED, and most of the world — falls back to
        // its code, which is the only honest thing to print for it.
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
        .format(major)
        // A code fallback is separated by a non-breaking space; one ordinary
        // space keeps it consistent with everything else on the page.
        .replace(/\u00A0/g, ' ')
    );
  } catch {
    return `${currency} ${major.toFixed(digits)}`;
  }
}
